import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useOptimizedCampaignData } from '@/contexts/OptimizedCampaignDataContext';
import { useAvailableProviders } from '@/hooks/useAvailableProviders';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchElevenLabsVoices,
  previewElevenLabsVoice,
  readCachedVoices,
  writeCachedVoices,
  EMPTY_VOICES_MESSAGE,
} from '@/lib/elevenLabsVoices';
import { CampaignPhoneNumberSelector } from './CampaignPhoneNumberSelector';
import { CampaignWizardData } from '@/types/unifiedCampaign';
import { ScriptCreator } from './ScriptCreator';
import { ProspectListCreator } from './ProspectListCreator';
import { useClients } from '@/hooks/useClients';
import { VapiKeySelector } from './VapiKeySelector';
import { usePhoneNumberValidation } from '@/hooks/usePhoneNumberValidation';
import { useOptimizedUnifiedCampaigns } from '@/hooks/useOptimizedUnifiedCampaigns';
import { getVapiKeyFromCampaign } from '@/utils/campaignValidation';
import {
  ChevronLeft,
  ChevronRight,
  Rocket,
  FileText,
  Users,
  Phone,
  Settings,
  Mic,
  Eye,
  X,
  Volume2,
} from 'lucide-react';

interface CampaignWizardProps {
  onComplete: (data: CampaignWizardData) => void;
  onCancel: () => void;
}

const STEPS = [
  { title: 'Campaign Info', icon: FileText },
  { title: 'Select Options', icon: Users },
  { title: 'Phone Settings', icon: Phone },
  { title: 'Call Behavior', icon: Settings },
  { title: 'AI & Voice', icon: Mic },
  { title: 'Review & Launch', icon: Eye },
];

export function CampaignWizard({ onComplete, onCancel }: CampaignWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [availableVoices, setAvailableVoices] = useState<any[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [showScriptCreator, setShowScriptCreator] = useState(false);
  const [showProspectListCreator, setShowProspectListCreator] = useState(false);
  const [scriptCreatorMode, setScriptCreatorMode] = useState<
    'outbound' | 'inbound' | null
  >(null);
  const {
    scripts,
    prospectLists,
    scriptsLoading,
    prospectListsLoading,
    refreshScripts,
    refreshProspectLists,
  } = useOptimizedCampaignData();
  const { availableProviders, hasAnyProvider } = useAvailableProviders();
  const { apiKeys } = useAuth();
  const { clients } = useClients();

  const { campaigns } = useOptimizedUnifiedCampaigns();
  const { validateAssignment, isPhoneNumberAvailableForCreation } =
    usePhoneNumberValidation(campaigns);

  const [wizardData, setWizardData] = useState<CampaignWizardData>({
    name: '',
    description: '',
    campaign_type: 'outbound',
    client_id: '',
    script_id: '',
    prospect_list_id: '',
    phone_provider: 'twilio',
    phone_number: '',
    max_retries: 3,
    call_interval: 5,
    max_call_duration: 30,
    working_hours_enabled: false,
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    working_hours_timezone: 'UTC',
    ai_model: 'gpt-4.1-2025-04-14',
    voice_provider: 'elevenlabs',
    voice_id: '',
    record_calls: true,
    tags: [],
  });

  // console.log('[CampaignWizard] API keys available in context:', apiKeys);
  const { toast } = useToast();

  // Check for integration keys
  const hasElevenLabsKeys = !!apiKeys?.elevenlabs;
  const hasCalendlyUrl = !!apiKeys?.calendlyUrl;
  const hasZapierWebhook = !!apiKeys?.zapierWebhook;
  const hasMakeWebhook = !!apiKeys?.makeWebhook;


  // Load cached voices on mount
  useEffect(() => {
    const cached = readCachedVoices();
    if (cached) setAvailableVoices(cached);
  }, []);

  // Auto-fetch voices when conditions are met
  useEffect(() => {
    const shouldFetchVoices =
      wizardData.voice_provider === 'elevenlabs' &&
      hasElevenLabsKeys &&
      availableVoices.length === 0 &&
      !loadingVoices;

    if (!shouldFetchVoices) return;

    const cached = readCachedVoices();
    if (cached) {
      setAvailableVoices(cached);
    } else {
      loadElevenLabsVoices();
    }
  }, [
    wizardData.voice_provider,
    hasElevenLabsKeys,
    availableVoices.length,
    loadingVoices,
    apiKeys?.elevenlabs,
  ]);

  const loadElevenLabsVoices = async () => {
    if (!apiKeys?.elevenlabs) {
      console.error('No ElevenLabs API key available');
      return;
    }

    setLoadingVoices(true);
    try {
      // console.log('Loading ElevenLabs voices...');
      const voices = await fetchElevenLabsVoices();

      setAvailableVoices(voices);
      writeCachedVoices(voices);

      if (voices.length > 0) {
        toast({
          title: 'Voices Loaded',
          description: `Loaded ${voices.length} ElevenLabs voices`,
        });
      } else {
        toast({
          title: 'No voices found',
          description: EMPTY_VOICES_MESSAGE,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error loading voices:', error);
      let errorMessage =
        'Failed to load ElevenLabs voices. Please check your API key.';

      if (error instanceof Error) {
        if (
          error.message.includes('unauthorized') ||
          error.message.includes('invalid_api_key')
        ) {
          errorMessage =
            'Invalid ElevenLabs API key. Please check your API settings.';
        } else if (error.message.includes('quota_exceeded')) {
          errorMessage =
            'ElevenLabs quota exceeded. Please check your account.';
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: 'Voice Loading Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoadingVoices(false);
    }
  };

  // Refresh voices function
  const refreshVoices = () => {
    writeCachedVoices([]);
    loadElevenLabsVoices();
    toast({
      title: 'Voices Refreshed',
      description: 'Voice list has been refreshed.',
    });
  };

  // Function to ensure API keys are loaded when needed (no longer needed with AuthContext)
  const ensureApiKeysLoaded = async () => {
    // This is now handled by AuthContext
    return;
  };

  // Preview voice functionality. Runs through the edge function so the user's
  // ElevenLabs key is never exposed to the browser.
  const previewVoice = async (voiceId: string) => {
    setPreviewingVoice(voiceId);
    try {
      const audioUrl = await previewElevenLabsVoice(voiceId);
      await new Audio(audioUrl).play();
      toast({
        title: 'Voice Preview',
        description: 'Playing voice sample...',
      });
    } catch (error) {
      console.error('Error previewing voice:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to preview voice';
      toast({
        title: 'Preview Error',
        description: errorMessage.includes('missing_permissions')
          ? "ElevenLabs API key requires 'Text to Speech' permissions. Please check your API key settings."
          : errorMessage,
        variant: 'destructive',
      });
    } finally {
      setPreviewingVoice(null);
    }
  };

  const updateData = (updates: Partial<CampaignWizardData>) => {
    setWizardData((prev) => ({ ...prev, ...updates }));
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      let nextStepIndex = currentStep + 1;

      // Skip Call Behavior step (step 3) for inbound campaigns
      if (nextStepIndex === 3 && wizardData.campaign_type === 'inbound') {
        nextStepIndex = 4;
      }
      //Skip Call Behaviour step (step 3) until we implement call retry and scheduled start
      if (nextStepIndex === 3) {
        nextStepIndex = 4;
      }

      setCurrentStep(nextStepIndex);

      // Refresh scripts when advancing to Step 2 (Select Options)
      if (currentStep === 0) {
        refreshScripts();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      let prevStepIndex = currentStep - 1;

      // Skip Call Behavior step (step 3) for inbound campaigns when going back
      if (prevStepIndex === 3 && wizardData.campaign_type === 'inbound') {
        prevStepIndex = 2;
      }
      //Skip Call Behaviour step (step 3) until we implement call retry and scheduled start
      if (prevStepIndex === 3) {
        prevStepIndex = 2;
      }

      setCurrentStep(prevStepIndex);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Campaign Info
        return wizardData.name.trim() !== '' && wizardData.campaign_type;
      case 1: // Select Options
        const hasRequiredScripts = (() => {
          if (wizardData.campaign_type === 'outbound') {
            return wizardData.script_id;
          } else if (wizardData.campaign_type === 'inbound') {
            return wizardData.script_id;
          } else if (wizardData.campaign_type === 'outbound_inbound') {
            return (
              wizardData.outbound_script_id && wizardData.inbound_script_id
            );
          }
          return false;
        })();
        const hasRequiredList =
          wizardData.campaign_type === 'inbound' || wizardData.prospect_list_id;
        const hasVapiKey = wizardData.vapi_key_name; // Now required in step 1
        return hasRequiredScripts && hasRequiredList && hasVapiKey;
      case 2: // Phone Settings
        return wizardData.phone_number; // phone_provider defaults to twilio, vapi_key_name from step 1
      case 3: // Call Behavior
        return true; // All have defaults
      case 4: // AI & Voice
        return wizardData.ai_model && wizardData.voice_id; // voice_provider defaults to elevenlabs
      default:
        return true;
    }
  };

  const handleComplete = () => {
    if (!canProceed()) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields before launching.',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone number and VAPI key assignment before completion
    if (wizardData.phone_number && wizardData.vapi_key_name) {
      const mockCampaign = {
        id: 'temp-validation-id',
        campaign_type: wizardData.campaign_type,
        phone_number: wizardData.phone_number,
        ai_settings: { vapi_key_name: wizardData.vapi_key_name },
      } as any;

      const validationResult = validateAssignment(
        mockCampaign,
        wizardData.phone_number,
        wizardData.vapi_key_name
      );
      if (!validationResult.isValid) {
        toast({
          title: 'Phone Number Assignment Error',
          description: validationResult.error,
          variant: 'destructive',
        });
        return;
      }
    }

    // Ensure ai_settings includes vapi_key_name
    const finalData = {
      ...wizardData,
      ai_settings: {
        ...wizardData.ai_settings,
        vapi_key_name: wizardData.vapi_key_name,
      },
    };

    onComplete(finalData);
  };

  // Get AI model options based on available providers
  const getAIModelOptions = () => {
    if (!hasAnyProvider) {
      return (
        <SelectItem value='no-provider' disabled>
          ⚠️ Setup API keys in Settings first
        </SelectItem>
      );
    }

    const options = [];

    if (availableProviders.includes('openai')) {
      options.push(
        <SelectItem key='gpt-4.1' value='gpt-4.1-2025-04-14'>
          OpenAI GPT-4.1 (Recommended)
        </SelectItem>,
        <SelectItem key='gpt-4o' value='gpt-4o'>
          OpenAI GPT-4o
        </SelectItem>,
        <SelectItem key='gpt-4o-mini' value='gpt-4o-mini'>
          OpenAI GPT-4o Mini (Faster)
        </SelectItem>
      );
    }

    if (availableProviders.includes('grok')) {
      options.push(
        <SelectItem key='grok' value='grok'>
          Grok (X.AI)
        </SelectItem>
      );
    }

    return options;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className='space-y-6'>
            <div className='space-y-2'>
              <Label htmlFor='name'>Campaign Name *</Label>
              <Input
                id='name'
                value={wizardData.name}
                onChange={(e) => updateData({ name: e.target.value })}
                placeholder='Enter campaign name'
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={wizardData.description || ''}
                onChange={(e) => updateData({ description: e.target.value })}
                placeholder='Describe your campaign goals...'
                rows={3}
              />
            </div>

            <div className='space-y-3'>
              <Label>Campaign Type *</Label>
              <RadioGroup
                value={wizardData.campaign_type}
                onValueChange={(value: any) =>
                  updateData({ campaign_type: value })
                }
              >
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value='outbound' id='outbound' />
                  <Label htmlFor='outbound'>⭑ Outbound</Label>
                </div>
                <div className='flex items-center space-x-2'>
                  <RadioGroupItem value='inbound' id='inbound' />
                  <Label htmlFor='inbound'>⭑ Inbound</Label>
                </div>
              </RadioGroup>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='client'>Client (Optional)</Label>
              <Select
                value={wizardData.client_id || 'none'}
                onValueChange={(value) =>
                  updateData({
                    client_id: value === 'none' ? undefined : value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select a client (optional)' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='none'>No Client</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-sm text-muted-foreground'>
                Assign this campaign to a specific client for organization and
                access control
              </p>
            </div>
          </div>
        );

      case 1:
        return (
          <div className='space-y-6'>
            {/* Outbound Script - shown for outbound and outbound_inbound */}
            {(wizardData.campaign_type === 'outbound' ||
              wizardData.campaign_type === 'outbound_inbound') && (
              <div className='space-y-2'>
                <Label htmlFor='outbound-script'>
                  {wizardData.campaign_type === 'outbound'
                    ? 'Outbound Script'
                    : 'Outbound Script'}{' '}
                  *
                </Label>
                <p className='text-sm text-muted-foreground'>
                  Used for initiated calls
                </p>
                <Select
                  value={
                    wizardData.campaign_type === 'outbound'
                      ? wizardData.script_id
                      : wizardData.outbound_script_id || ''
                  }
                  onValueChange={(value) => {
                    if (value === 'create-new') {
                      setScriptCreatorMode('outbound');
                      setShowScriptCreator(true);
                      return;
                    }
                    if (value === 'refresh-list') {
                      refreshScripts();
                      toast({
                        title: 'List Refreshed',
                        description: 'Script list has been refreshed.',
                      });
                      return;
                    }
                    updateData(
                      wizardData.campaign_type === 'outbound'
                        ? { script_id: value }
                        : { outbound_script_id: value }
                    );
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select an outbound script' />
                  </SelectTrigger>
                  <SelectContent>
                    {scripts
                      .filter((script) => script.mode === 'outbound')
                      .map((script) => (
                        <SelectItem key={script.id} value={script.id}>
                          {script.name} - {script.target_industry}
                        </SelectItem>
                      ))}
                    {scripts.filter((script) => script.mode === 'outbound')
                      .length > 0 && (
                      <div className='border-t border-border my-1' />
                    )}
                    <SelectItem
                      value='refresh-list'
                      className='text-muted-foreground'
                    >
                      <div className='flex items-center gap-2'>
                        <span className='text-lg'>🔄</span>
                        Refresh List
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Inbound Script - shown for inbound and outbound_inbound */}
            {(wizardData.campaign_type === 'inbound' ||
              wizardData.campaign_type === 'outbound_inbound') && (
              <div className='space-y-2'>
                <Label htmlFor='inbound-script'>Inbound Script *</Label>
                <p className='text-sm text-muted-foreground'>
                  Used for fallback or inbound pickup
                </p>
                <Select
                  value={
                    wizardData.campaign_type === 'inbound'
                      ? wizardData.script_id
                      : wizardData.inbound_script_id || ''
                  }
                  onValueChange={(value) => {
                    if (value === 'create-new') {
                      setScriptCreatorMode('inbound');
                      setShowScriptCreator(true);
                      return;
                    }
                    if (value === 'refresh-list') {
                      refreshScripts();
                      toast({
                        title: 'List Refreshed',
                        description: 'Script list has been refreshed.',
                      });
                      return;
                    }
                    updateData(
                      wizardData.campaign_type === 'inbound'
                        ? { script_id: value }
                        : { inbound_script_id: value }
                    );
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select an inbound script' />
                  </SelectTrigger>
                  <SelectContent>
                    {scripts
                      .filter((script) => script.mode === 'inbound')
                      .map((script) => (
                        <SelectItem key={script.id} value={script.id}>
                          {script.name} - {script.target_industry}
                        </SelectItem>
                      ))}
                    {scripts.filter((script) => script.mode === 'inbound')
                      .length > 0 && (
                      <div className='border-t border-border my-1' />
                    )}
                    <SelectItem
                      value='refresh-list'
                      className='text-muted-foreground'
                    >
                      <div className='flex items-center gap-2'>
                        <span className='text-lg'>🔄</span>
                        Refresh List
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Prospect List - shown for outbound and outbound_inbound only */}
            {(wizardData.campaign_type === 'outbound' ||
              wizardData.campaign_type === 'outbound_inbound') && (
              <div className='space-y-2'>
                <Label htmlFor='prospectList'>Prospect List *</Label>
                <Select
                  value={wizardData.prospect_list_id || ''}
                  onValueChange={(value) => {
                    if (value === 'create-new') {
                      setShowProspectListCreator(true);
                      return;
                    }
                    if (value === 'refresh-list') {
                      refreshProspectLists();
                      toast({
                        title: 'List Refreshed',
                        description: 'Prospect list has been refreshed.',
                      });
                      return;
                    }
                    updateData({ prospect_list_id: value });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select a prospect list' />
                  </SelectTrigger>
                  <SelectContent>
                    {prospectLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name} ({list.prospects.length} prospects)
                      </SelectItem>
                    ))}
                    {prospectLists.length > 0 && (
                      <div className='border-t border-border my-1' />
                    )}
                    <SelectItem
                      value='refresh-list'
                      className='text-muted-foreground'
                    >
                      <div className='flex items-center gap-2'>
                        <span className='text-lg'>🔄</span>
                        Refresh List
                      </div>
                    </SelectItem>
                    <SelectItem
                      value='create-new'
                      className='text-primary font-medium'
                    >
                      <div className='flex items-center gap-2'>
                        <span className='text-lg'>➕</span>
                        Create New Prospect List
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* VAPI Key Selection - shown after scripts, conditional on multiple keys */}
            <div className='space-y-2'>
              <VapiKeySelector
                value={wizardData.vapi_key_name || ''}
                onChange={(value) => updateData({ vapi_key_name: value })}
                label='VAPI Account *'
                description='Select which VAPI account to use for this campaign'
              />
            </div>

            {/* Tags - shown for all campaign types */}
            <div className='space-y-2'>
              <Label htmlFor='tags'>Tags (optional)</Label>
              <Input
                id='tags'
                value={wizardData.tags?.join(', ') || ''}
                onChange={(e) =>
                  updateData({
                    tags: e.target.value
                      ? e.target.value.split(',').map((tag) => tag.trim())
                      : [],
                  })
                }
                placeholder='Enter tags separated by commas'
              />
            </div>
          </div>
        );

      case 2:
        // Ensure phone provider defaults to twilio
        if (!wizardData.phone_provider) {
          updateData({ phone_provider: 'twilio' });
        }

        return (
          <div className='space-y-6'>
            {/* Show VAPI account info (from resolved step 1) */}
            {wizardData.vapi_key_name && (
              <div className='p-3 bg-muted/50 rounded-lg'>
                <p className='text-sm text-muted-foreground'>
                  <span className='font-medium'>Using VAPI Account:</span>{' '}
                  {(() => {
                    // Get friendly name from VapiKeySelector logic
                    if (wizardData.vapi_key_name === 'api_key')
                      return 'Primary Account';
                    if (wizardData.vapi_key_name === 'account_b')
                      return 'Alt Account B';
                    return wizardData.vapi_key_name;
                  })()}
                </p>
              </div>
            )}

            <div className='space-y-2'>
              <Label htmlFor='phoneNumber'>Phone Number *</Label>
              <CampaignPhoneNumberSelector
                value={wizardData.phone_number || ''}
                onChange={(value) => updateData({ phone_number: value })}
                selectedVapiKey=''
                selectedVapiKeyName={wizardData.vapi_key_name}
              />
              {/* Show validation error if phone number conflicts */}
              {wizardData.phone_number &&
                wizardData.vapi_key_name &&
                (() => {
                  const isAvailable = isPhoneNumberAvailableForCreation(
                    wizardData.phone_number,
                    wizardData.vapi_key_name
                  );
                  if (!isAvailable) {
                    // For creation, only cross-VAPI conflicts should be blocked
                    const crossVapiConflict = campaigns.find(
                      (campaign) =>
                        campaign.phone_number === wizardData.phone_number &&
                        campaign.status !== 'cancelled' &&
                        campaign.status !== 'completed' &&
                        getVapiKeyFromCampaign(campaign) !==
                          wizardData.vapi_key_name
                    );

                    const errorMessage = crossVapiConflict
                      ? `⚠️ This phone number is already assigned to campaign "${crossVapiConflict.name}" using a different VAPI account. Cannot mix VAPI accounts on the same phone number.`
                      : '⚠️ This phone number is not available for creation.';

                    return (
                      <div className='mt-2 p-3 bg-destructive/5 border border-destructive/20 rounded-lg'>
                        <p className='text-sm text-destructive'>
                          {errorMessage}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
            </div>
          </div>
        );

      case 3:
        // Skip this step entirely if campaign is inbound
        if (wizardData.campaign_type === 'inbound') {
          return null;
        }
        //Temporarly skip this step, until we implement call retry and scheduled start
        return null;
        return (
          <div className='space-y-6'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='maxRetries'>Max Retries</Label>
                <Input
                  id='maxRetries'
                  type='number'
                  value={wizardData.max_retries}
                  onChange={(e) =>
                    updateData({ max_retries: parseInt(e.target.value) || 3 })
                  }
                  min='0'
                  max='10'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='callInterval'>Call Interval (minutes)</Label>
                <Input
                  id='callInterval'
                  type='number'
                  value={wizardData.call_interval}
                  onChange={(e) =>
                    updateData({ call_interval: parseInt(e.target.value) || 5 })
                  }
                  min='1'
                  max='60'
                />
              </div>
            </div>

            <div className='space-y-4'>
              <div className='flex items-center space-x-2'>
                <Switch
                  id='workingHours'
                  checked={wizardData.working_hours_enabled}
                  onCheckedChange={(checked) =>
                    updateData({ working_hours_enabled: checked })
                  }
                />
                <Label htmlFor='workingHours'>Working Hours</Label>
              </div>

              {wizardData.working_hours_enabled && (
                <div className='grid grid-cols-2 gap-4 ml-6'>
                  <div className='space-y-2'>
                    <Label htmlFor='startTime'>Start Time</Label>
                    <Input
                      id='startTime'
                      type='time'
                      value={wizardData.working_hours_start}
                      onChange={(e) =>
                        updateData({ working_hours_start: e.target.value })
                      }
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='endTime'>End Time</Label>
                    <Input
                      id='endTime'
                      type='time'
                      value={wizardData.working_hours_end}
                      onChange={(e) =>
                        updateData({ working_hours_end: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='scheduledStart'>Scheduled Start (optional)</Label>
              <Input
                id='scheduledStart'
                type='datetime-local'
                value={
                  wizardData.scheduled_start
                    ? wizardData.scheduled_start.toISOString().slice(0, 16)
                    : ''
                }
                onChange={(e) =>
                  updateData({
                    scheduled_start: e.target.value
                      ? new Date(e.target.value)
                      : undefined,
                  })
                }
              />
            </div>
          </div>
        );

      case 4:
        // Ensure voice provider defaults to elevenlabs
        if (!wizardData.voice_provider) {
          updateData({ voice_provider: 'elevenlabs' });
        }

        return (
          <div className='space-y-6'>
            <div className='space-y-2'>
              <Label htmlFor='aiModel'>AI Model *</Label>
              <Select
                value={wizardData.ai_model}
                onValueChange={(value: any) => updateData({ ai_model: value })}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !hasAnyProvider
                        ? '⚠️ Setup API keys first'
                        : 'Select AI model'
                    }
                  />
                </SelectTrigger>
                <SelectContent>{getAIModelOptions()}</SelectContent>
              </Select>
              {!hasAnyProvider ? (
                <p className='text-sm text-muted-foreground'>
                  Go to API Settings to configure your AI provider keys (Grok or
                  OpenAI).
                </p>
              ) : (
                <p className='text-sm text-muted-foreground'>
                  Model selection is filtered by your connected API keys. VAPI
                  will use keys from your VAPI account if not provided here.
                </p>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='voice'>Voice *</Label>
              {wizardData.voice_provider === 'elevenlabs' ? (
                <div className='space-y-2'>
                  <Select
                    disabled={!hasElevenLabsKeys || loadingVoices}
                    value={wizardData.voice_id || ''}
                    onValueChange={(value) => {
                      if (value === 'refresh-voices') {
                        refreshVoices();
                        return;
                      }
                      updateData({ voice_id: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !hasElevenLabsKeys
                            ? 'Configure ElevenLabs API key in API Settings to access voice selection.'
                            : loadingVoices
                            ? 'Loading ElevenLabs voices...'
                            : availableVoices.length === 0
                            ? 'No voices available'
                            : 'Select voice'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {!hasElevenLabsKeys ? (
                        <SelectItem value='disabled' disabled>
                          Configure ElevenLabs API key in API Settings to access
                          voice selection.
                        </SelectItem>
                      ) : loadingVoices ? (
                        <SelectItem value='loading' disabled>
                          Loading ElevenLabs voices...
                        </SelectItem>
                      ) : availableVoices.length === 0 ? (
                        <SelectItem value='none' disabled>
                          No voices available
                        </SelectItem>
                      ) : (
                        <>
                          {availableVoices.map((voice) => (
                            <SelectItem
                              key={voice.voice_id}
                              value={voice.voice_id}
                            >
                              {voice.name}
                            </SelectItem>
                          ))}
                          {availableVoices.length > 0 && (
                            <div className='border-t border-border my-1' />
                          )}
                          <SelectItem
                            value='refresh-voices'
                            className='text-muted-foreground'
                          >
                            <div className='flex items-center gap-2'>
                              <span className='text-lg'>🔄</span>
                              Refresh Voices
                            </div>
                          </SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>

                  {/* Voice Preview Button */}
                  {wizardData.voice_id && (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => previewVoice(wizardData.voice_id!)}
                      disabled={previewingVoice === wizardData.voice_id}
                      className='mt-2'
                    >
                      <Volume2 className='h-4 w-4 mr-2' />
                      {previewingVoice === wizardData.voice_id
                        ? 'Playing...'
                        : '🔊 Preview Voice'}
                    </Button>
                  )}
                </div>
              ) : (
                <Select
                  value={wizardData.voice_id || ''}
                  onValueChange={(value) => updateData({ voice_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select voice' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='alloy'>Alloy</SelectItem>
                    <SelectItem value='echo'>Echo</SelectItem>
                    <SelectItem value='fable'>Fable</SelectItem>
                    <SelectItem value='onyx'>Onyx</SelectItem>
                    <SelectItem value='nova'>Nova</SelectItem>
                    <SelectItem value='shimmer'>Shimmer</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className='space-y-6'>
            <div className='text-center mb-6'>
              <Rocket className='h-12 w-12 mx-auto text-primary mb-2' />
              <h3 className='text-xl font-semibold'>Ready to Launch!</h3>
              <p className='text-muted-foreground'>
                Review your campaign settings below
              </p>
            </div>

            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4 text-sm'>
                <div>
                  <span className='font-medium'>Campaign:</span>{' '}
                  {wizardData.name}
                </div>
                <div>
                  <span className='font-medium'>Type:</span>
                  <Badge variant='secondary' className='ml-2'>
                    {wizardData.campaign_type.replace('_', ' & ')}
                  </Badge>
                </div>
                <div>
                  <span className='font-medium'>Script:</span>{' '}
                  {scripts.find((s) => s.id === wizardData.script_id)?.name}
                </div>
                {wizardData.prospect_list_id && (
                  <div>
                    <span className='font-medium'>Prospects:</span>{' '}
                    {
                      prospectLists.find(
                        (l) => l.id === wizardData.prospect_list_id
                      )?.prospects.length
                    }
                  </div>
                )}
                <div>
                  <span className='font-medium'>Phone:</span>{' '}
                  {wizardData.phone_number}
                </div>
                <div>
                  <span className='font-medium'>Provider:</span>{' '}
                  {wizardData.phone_provider}
                </div>
                <div>
                  <span className='font-medium'>Voice:</span>{' '}
                  {availableVoices.find(
                    (v) => v.voice_id === wizardData.voice_id
                  )?.name || wizardData.voice_id}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Card className='max-w-4xl mx-auto'>
        <CardHeader className='pb-4'>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                {(() => {
                  const IconComponent = STEPS[currentStep].icon;
                  return <IconComponent className='h-5 w-5' />;
                })()}
                {STEPS[currentStep].title}
              </CardTitle>
              <CardDescription>
                Step {currentStep + 1} of {STEPS.length}
              </CardDescription>
            </div>
            <Button variant='ghost' size='sm' onClick={onCancel}>
              <X className='h-4 w-4' />
            </Button>
          </div>

          {/* Progress indicator */}
          <div className='flex space-x-2 mt-4'>
            {STEPS.map((step, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded ${
                  index <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </CardHeader>

        <CardContent className='space-y-6'>
          {renderStep()}

          <Separator />

          <div className='flex justify-between'>
            <Button
              variant='outline'
              onClick={prevStep}
              disabled={currentStep === 0}
            >
              <ChevronLeft className='h-4 w-4 mr-2' />
              Previous
            </Button>

            {currentStep === STEPS.length - 1 ? (
              <Button onClick={handleComplete} disabled={!canProceed()}>
                <Rocket className='h-4 w-4 mr-2' />
                Launch Campaign
              </Button>
            ) : (
              <Button onClick={nextStep} disabled={!canProceed()}>
                Next
                <ChevronRight className='h-4 w-4 ml-2' />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <ScriptCreator
        open={showScriptCreator}
        onOpenChange={(open) => {
          setShowScriptCreator(open);
          if (!open) {
            setScriptCreatorMode(null);
          }
        }}
        preselectedMode={scriptCreatorMode}
        onScriptCreated={(script) => {
          // Refresh scripts first to ensure new script is in list
          refreshScripts();

          // Use setTimeout to ensure the script list has been updated before selecting
          setTimeout(() => {
            // Auto-select the newly created script
            if (scriptCreatorMode === 'outbound') {
              if (wizardData.campaign_type === 'outbound') {
                updateData({ script_id: script.id });
              } else {
                updateData({ outbound_script_id: script.id });
              }
            } else if (scriptCreatorMode === 'inbound') {
              if (wizardData.campaign_type === 'inbound') {
                updateData({ script_id: script.id });
              } else {
                updateData({ inbound_script_id: script.id });
              }
            }

            toast({
              title: 'Script Selected',
              description: `"${script.name}" has been automatically selected for your campaign.`,
            });
          }, 200);

          setShowScriptCreator(false);
          setScriptCreatorMode(null);
        }}
      />

      <ProspectListCreator
        open={showProspectListCreator}
        onOpenChange={setShowProspectListCreator}
        onListCreated={(list) => {
          // Auto-select the newly created list
          setTimeout(() => {
            updateData({ prospect_list_id: list.id });
            toast({
              title: 'Prospect List Selected',
              description: `"${list.name}" has been automatically selected for your campaign.`,
            });
          }, 100);
          setShowProspectListCreator(false);
        }}
      />
    </>
  );
}
